import React, { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash.debounce';
import axios from '../../utils/axiosConfig';
import { setAuthToken } from '../../utils/auth';
import { Calendar, Trash2, Edit, Plus, ArrowUp, ArrowDown, FileText, Image, Info } from 'lucide-react';
import '../../styles/EventsManagement.css';
import { InfoTooltip } from '../Common/InfoTooltip';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function EventsManagement() {
  const [sections, setSections] = useState([]);
  const [liveEvents, setLiveEvents] = useState([]);
  const [activeView, setActiveView] = useState('sections');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  
  // Section state
  const [editingSection, setEditingSection] = useState(null);
  const [isSavingSection, setIsSavingSection] = useState(false);
  
  // Event state
  const [editingEvent, setEditingEvent] = useState(null);
  const [isSavingEvent, setIsSavingEvent] = useState(false);
  
  // Option state
  const [uploadingOptionId, setUploadingOptionId] = useState(null);
  const [uploadingEventId, setUploadingEventId] = useState(null);
  const [updatingOptionId, setUpdatingOptionId] = useState(null);
  const [editingOption, setEditingOption] = useState(null);
  
  // Form state
  const [newSection, setNewSection] = useState({
    title: '',
    description: ''
  });

  const [newOption, setNewOption] = useState({
    sectionId: null,
    subtitle: '',
    file: null
  });

  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    file: null
  });

  const [editedOption, setEditedOption] = useState({
    subtitle: '',
    file: null
  });

  // Confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: '',
    message: '',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    onConfirm: null,
    type: 'danger'
  });

  const debouncedFetchData = useCallback(
    debounce(async () => {
      setIsLoading(true);
      try {
        const [sectionsRes, eventsRes] = await Promise.all([
          axios.get('/api/vendors/event-sections'),
          axios.get('/api/vendors/live-events')
        ]);
        setSections(sectionsRes.data);
        setLiveEvents(eventsRes.data);
        setError(null);
      } catch (error) {
        console.error('Error fetching events data:', error);
        setError('Failed to fetch events data');
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setAuthToken(token);
    }
    debouncedFetchData();
    
    return () => {
      debouncedFetchData.cancel();
    };
  }, [debouncedFetchData]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [sectionsRes, eventsRes] = await Promise.all([
        axios.get('/api/vendors/event-sections'),
        axios.get('/api/vendors/live-events')
      ]);
      setSections(sectionsRes.data);
      setLiveEvents(eventsRes.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch events data');
      console.error(err);
    } finally {
     setIsLoading(false);
    }
  };

  // Helper to show toast messages
  const showToast = (type, message) => {
    setToast({ type, message });
    // Auto clear after 3 seconds
    setTimeout(() => setToast(null), 3000);
  };

  // File validation helper
  const validateFile = (file) => {
    if (!file) return { valid: true };
    
    if (file.size > MAX_FILE_SIZE) {
      return { 
        valid: false, 
        error: `File size exceeds the maximum limit of 10MB` 
      };
    }
    
    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const validDocTypes = ['application/pdf'];
    const validTypes = [...validImageTypes, ...validDocTypes];
    
    if (!validTypes.includes(file.type)) {
      return { 
        valid: false, 
        error: `File type not supported. Please upload an image (JPEG, PNG, GIF, WebP) or PDF.` 
      };
    }
    
    return { valid: true };
  };

  const handleEditSection = (section) => {
    if (editingSection?.id === section.id) {
      setEditingSection(null);
      setNewSection({ title: '', description: '' });
    } else {
      setEditingSection(section);
      setNewSection({
        title: section.title,
        description: section.description || ''
      });
    }
  };

  const handleEditEvent = (event) => {
    if (editingEvent?.id === event.id) {
      setEditingEvent(null);
      setNewEvent({
        title: '',
        description: '',
        event_date: '',
        event_time: '',
        file: null
      });
    } else {
      setEditingEvent(event);
      const eventDate = new Date(event.event_date);
      setNewEvent({
        title: event.title,
        description: event.description || '',
        event_date: eventDate.toISOString().split('T')[0],
        event_time: eventDate.toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit' }),
        file: null
      });
    }
  };

  const handleReorder = async (sectionId, direction) => {
    try {
      const currentIndex = sections.findIndex(section => section.id === sectionId);
      if ((direction === 'up' && currentIndex === 0) || 
          (direction === 'down' && currentIndex === sections.length - 1)) {
        return;
      }

      await axios.put(`/api/vendors/event-sections/${sectionId}`, {
        sort_order: direction === 'up' ? currentIndex - 1 : currentIndex + 1
      });
      
      fetchData();
      showToast('success', 'Section order updated');
    } catch (error) {
      setError('Failed to reorder sections');
      showToast('error', 'Failed to update section order');
      console.error(error);
    }
  };

  const handleSectionSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!newSection.title.trim()) {
      showToast('error', 'Section title is required');
      return;
    }
    
    setIsSavingSection(true);
    try {
      if (editingSection) {
        await axios.put(`/api/vendors/event-sections/${editingSection.id}`, newSection);
        showToast('success', 'Section updated successfully');
      } else {
        await axios.post('/api/vendors/event-sections', newSection);
        showToast('success', 'Section created successfully');
      }
      setNewSection({ title: '', description: '' });
      setEditingSection(null);
      await fetchData();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Error saving section');
    } finally {
      setIsSavingSection(false);
    }
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!newEvent.title.trim()) {
      showToast('error', 'Event title is required');
      return;
    }
    
    if (!newEvent.event_date) {
      showToast('error', 'Event date is required');
      return;
    }
    
    if (!newEvent.event_time) {
      showToast('error', 'Event time is required');
      return;
    }
    
    // Validate file if present
    if (newEvent.file) {
      const validation = validateFile(newEvent.file);
      if (!validation.valid) {
        showToast('error', validation.error);
        return;
      }
    }
    
    const formData = new FormData();
    const dateTime = `${newEvent.event_date}T${newEvent.event_time}:00`;
    
    Object.keys(newEvent).forEach(key => {
      if (key === 'event_date') {
        formData.append('event_date', dateTime);
      } else if (key === 'event_time') {
        return;
      } else if (key === 'file' && newEvent.file) {
        formData.append('file', newEvent.file);
      } else {
        formData.append(key, newEvent[key]);
      }
    });
  
    const eventId = editingEvent?.id || 'new';
    setUploadingEventId(eventId);
    setIsSavingEvent(true);
  
    try {
      if (editingEvent) {
        await axios.put(`/api/vendors/live-events/${editingEvent.id}`, formData);
        showToast('success', 'Event updated successfully');
      } else {
        await axios.post('/api/vendors/live-events', formData);
        showToast('success', 'Event created successfully');
      }
      setNewEvent({
        title: '',
        description: '',
        event_date: '',
        event_time: '',
        file: null
      });
      setEditingEvent(null);
      await fetchData();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Error saving event');
    } finally {
      setUploadingEventId(null);
      setIsSavingEvent(false);
    }
  };

  const handleAddOption = async (sectionId) => {
    if (!newOption.subtitle.trim()) {
      showToast('error', 'Option subtitle is required');
      return;
    }
    
    // Validate file if present
    if (newOption.file) {
      const validation = validateFile(newOption.file);
      if (!validation.valid) {
        showToast('error', validation.error);
        return;
      }
    }
  
    setUploadingOptionId(sectionId);
    const formData = new FormData();
    formData.append('subtitle', newOption.subtitle);
    if (newOption.file) {
      formData.append('file', newOption.file);
    }
  
    try {
      await axios.post(`/api/vendors/event-sections/${sectionId}/options`, formData);
      setNewOption({
        sectionId: null,
        subtitle: '',
        file: null
      });
      showToast('success', 'Option added successfully');
      fetchData();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Error adding option');
    } finally {
      setUploadingOptionId(null);
    }
  };

  const handleDeleteSection = (section) => {
    setConfirmDialog({
      show: true,
      title: 'Delete Section',
      message: `Are you sure you want to delete the section "${section.title}"? This will also delete all options within this section and cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger',
      onConfirm: async () => {
        try {
          await axios.delete(`/api/vendors/event-sections/${section.id}`);
          fetchData();
          showToast('success', 'Section deleted successfully');
        } catch (error) {
          showToast('error', error.response?.data?.error || 'Error deleting section');
        } finally {
          setConfirmDialog({ show: false });
        }
      }
    });
  };

  const handleDeleteEvent = (event) => {
    setConfirmDialog({
      show: true,
      title: 'Delete Event',
      message: `Are you sure you want to delete the event "${event.title}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger',
      onConfirm: async () => {
        try {
          await axios.delete(`/api/vendors/live-events/${event.id}`);
          fetchData();
          showToast('success', 'Event deleted successfully');
        } catch (error) {
          showToast('error', error.response?.data?.error || 'Error deleting event');
        } finally {
          setConfirmDialog({ show: false });
        }
      }
    });
  };

  const handleEditOption = (option) => {
    if (editingOption?.id === option.id) {
      setEditingOption(null);
      setEditedOption({ subtitle: '', file: null });
    } else {
      setEditingOption(option);
      setEditedOption({
        subtitle: option.subtitle,
        file: null // Can't pre-fill file input
      });
    }
  };
  
  const handleUpdateOption = async (sectionId, optionId) => {
    if (!editedOption.subtitle.trim()) {
      showToast('error', 'Option subtitle is required');
      return;
    }
    
    // Validate file if present
    if (editedOption.file) {
      const validation = validateFile(editedOption.file);
      if (!validation.valid) {
        showToast('error', validation.error);
        return;
      }
    }
    
    const formData = new FormData();
    formData.append('subtitle', editedOption.subtitle);
    if (editedOption.file) {
      formData.append('file', editedOption.file);
    }
  
    setUpdatingOptionId(optionId);
  
    try {
      await axios.put(`/api/vendors/event-options/${optionId}`, formData);
      setEditingOption(null);
      setEditedOption({ subtitle: '', file: null });
      showToast('success', 'Option updated successfully');
      fetchData();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Error updating option');
    } finally {
      setUpdatingOptionId(null);
    }
  };
  
  const handleDeleteOption = (sectionId, option) => {
    setConfirmDialog({
      show: true,
      title: 'Delete Option',
      message: `Are you sure you want to delete the option "${option.subtitle}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger',
      onConfirm: async () => {
        try {
          await axios.delete(`/api/vendors/event-options/${option.id}`);
          fetchData();
          showToast('success', 'Option deleted successfully');
        } catch (error) {
          showToast('error', error.response?.data?.error || 'Error deleting option');
        } finally {
          setConfirmDialog({ show: false });
        }
      }
    });
  };

  // File input handlers
  const handleFileChange = (e, target) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const validation = validateFile(file);
    if (!validation.valid) {
      showToast('error', validation.error);
      e.target.value = null; // Reset the input
      return;
    }
    
    if (target === 'event') {
      setNewEvent({...newEvent, file});
    } else if (target === 'option') {
      setNewOption({...newOption, file});
    } else if (target === 'editOption') {
      setEditedOption({...editedOption, file});
    }
  };

  if (isLoading && !sections.length && !liveEvents.length) {
    return (
      <div className="em-loading-container">
        <div className="em-spinner"></div>
        <span className="em-loading-text">Loading events management...</span>
      </div>
    );
  }

  return (
    <div className="em-container">
      {/* Toast notification */}
      {toast && (
        <div className={`em-toast ${toast.type}`}>
          <div className="em-toast-content">
            <span className={`em-toast-icon ${toast.type}`}>
              {toast.type === 'success' ? '✓' : '✕'}
            </span>
            <span className="em-toast-message">{toast.message}</span>
          </div>
        </div>
      )}
      
      {/* Header section */}
      <div className="em-header">
        <div className="em-header-content">
          <h1>Events Management</h1>
          <p className="em-header-subtitle">Create and manage event occasions and upcoming events</p>
        </div>
      </div>
      
      {/* Tab navigation */}
      <div className="em-tabs">
        <button
          className={`em-tab ${activeView === 'sections' ? 'active' : ''}`}
          onClick={() => setActiveView('sections')}
        >
          <FileText size={18} />
          Event Occasions
        </button>
        <button
          className={`em-tab ${activeView === 'live' ? 'active' : ''}`}
          onClick={() => setActiveView('live')}
        >
          <Calendar size={18} />
          Upcoming Events
        </button>
      </div>
  
      {error && (
        <div className="em-error-message">
          <div className="em-error-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <span>{error}</span>
          <button onClick={fetchData} className="em-button secondary">Retry</button>
        </div>
      )}
  
      {activeView === 'sections' ? (
        <div className="em-sections-view">
          {/* Section creation/editing form */}
          <div className="em-card">
            <div className="em-card-header">
              <h2>{editingSection ? 'Edit Section' : 'Add New Event Section'}</h2>
              {editingSection && (
                <button 
                  type="button" 
                  onClick={() => {
                    setEditingSection(null);
                    setNewSection({ title: '', description: '' });
                  }} 
                  className="em-button-icon"
                  aria-label="Cancel editing"
                >
                  ×
                </button>
              )}
            </div>
            <div className="em-card-body">
              <form onSubmit={handleSectionSubmit} className="em-form">
                <div className="em-form-group">
                  <label className="em-label" htmlFor="section-title">
                    Section Title <span className="em-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="section-title"
                    className="em-input"
                    placeholder="e.g., Wedding Packages, Corporate Events"
                    value={newSection.title}
                    onChange={(e) => setNewSection({...newSection, title: e.target.value})}
                    required
                  />
                </div>
                <div className="em-form-group">
                  <label className="em-label" htmlFor="section-description">
                    Description
                    <InfoTooltip 
                      text="Optional description to provide more details about this event section" 
                      variant="vendor"
                    />
                  </label>
                  <textarea
                    id="section-description"
                    className="em-input em-textarea"
                    placeholder="Enter additional details about this event section (optional)"
                    value={newSection.description}
                    onChange={(e) => setNewSection({...newSection, description: e.target.value})}
                    rows="3"
                  />
                </div>
                <div className="em-form-actions">
                  {editingSection && (
                    <button 
                      type="button" 
                      onClick={() => {
                        setEditingSection(null);
                        setNewSection({ title: '', description: '' });
                      }} 
                      className="em-button secondary"
                    >
                      Cancel
                    </button>
                  )}
                  <button 
                    type="submit" 
                    className="em-button primary"
                    disabled={isSavingSection}
                  >
                    {isSavingSection ? (
                      <>
                        <span className="em-spinner"></span> 
                        {editingSection ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingSection ? 'Update Section' : 'Create Section'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
          
          {/* Section List */}
          <h3 className="em-section-title">Your Event Sections</h3>
          
          {sections.length === 0 ? (
            <div className="em-empty-state">
              <div className="em-empty-icon">
                <FileText size={40} />
              </div>
              <h3>No event sections yet</h3>
              <p>Create your first event section to organize different types of events you offer</p>
            </div>
          ) : (
            <div className="em-sections-list">
              {sections.map((section, index) => (
                <div key={section.id} className="em-section-item">
                  <div className="em-section-header">
                    <div className="em-section-title-area">
                      <h4>{section.title}</h4>
                      <div className="em-order-controls">
                        <button
                          className="em-icon-button"
                          onClick={() => handleReorder(section.id, 'up')}
                          disabled={index === 0}
                          aria-label="Move section up"
                        >
                          <ArrowUp size={16} />
                        </button>
                        <button
                          className="em-icon-button"
                          onClick={() => handleReorder(section.id, 'down')}
                          disabled={index === sections.length - 1}
                          aria-label="Move section down"
                        >
                          <ArrowDown size={16} />
                        </button>
                      </div>
                    </div>
                    <div className="em-section-actions">
                      <button 
                        className="em-icon-button with-text"
                        onClick={() => handleEditSection(section)}
                      >
                        <Edit size={16} />
                        <span>{editingSection?.id === section.id ? 'Cancel Edit' : 'Edit'}</span>
                      </button>
                      <button 
                        className="em-icon-button with-text danger"
                        onClick={() => handleDeleteSection(section)}
                      >
                        <Trash2 size={16} />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                  
                  {section.description && (
                    <p className="em-section-description">{section.description}</p>
                  )}
                  
                  <div className="em-info-box">
                    <Info size={18} />
                    <span>
                      Create options for this event section that customers can book. 
                      You can add images or PDFs to showcase each option.
                    </span>
                  </div>
                  
                  {/* Options list */}
                  <div className="em-options-list">
                    {section.options && section.options.length > 0 ? (
                      section.options.map(option => (
                        <div key={option.id} className="em-option-item">
                          {editingOption?.id === option.id ? (
                            // Edit mode
                            <div className="em-option-edit-form">
                              <div className="em-form-group">
                                <label className="em-label" htmlFor={`option-edit-title-${option.id}`}>
                                  Option Title <span className="em-required">*</span>
                                </label>
                                <input
                                  id={`option-edit-title-${option.id}`}
                                  type="text"
                                  value={editedOption.subtitle}
                                  onChange={(e) => setEditedOption({
                                    ...editedOption,
                                    subtitle: e.target.value
                                  })}
                                  placeholder="Option Title"
                                  className="em-input"
                                  disabled={updatingOptionId === option.id}
                                />
                              </div>
                              
                              <div className="em-form-group">
                                <label className="em-label" htmlFor={`option-edit-file-${option.id}`}>
                                  Replace File (PDF or Image)
                                  <InfoTooltip 
                                    text="Upload a new file to replace the current one (optional)" 
                                    variant="vendor"
                                  />
                                </label>
                                <div className="em-file-input-container">
                                  <input
                                    id={`option-edit-file-${option.id}`}
                                    type="file"
                                    accept="image/*,application/pdf"
                                    onChange={(e) => handleFileChange(e, 'editOption')}
                                    className="em-file-input"
                                    disabled={updatingOptionId === option.id}
                                  />
                                  <div className="em-file-input-overlay">
                                    <div className="em-file-input-button">
                                      Choose File
                                    </div>
                                    <span className="em-file-name">
                                      {editedOption.file ? editedOption.file.name : 'No file chosen'}
                                    </span>
                                  </div>
                                </div>
                                <div className="em-file-size-info">
                                  Maximum file size: 10MB. Supported formats: PDF, JPEG, PNG, GIF, WebP
                                </div>
                              </div>
                              
                              <div className="em-form-actions">
                                <button 
                                  type="button"
                                  onClick={() => handleEditOption(option)}
                                  className="em-button secondary"
                                  disabled={updatingOptionId === option.id}
                                >
                                  Cancel
                                </button>
                                <button 
                                  type="button"
                                  onClick={() => handleUpdateOption(section.id, option.id)}
                                  className="em-button primary"
                                  disabled={updatingOptionId === option.id}
                                >
                                  {updatingOptionId === option.id ? (
                                    <>
                                      <span className="em-spinner"></span> 
                                      Updating...
                                    </>
                                  ) : (
                                    'Update Option'
                                  )}
                                </button>
                              </div>
                            </div>
                          ) : (
                            // View mode
                            <>
                              <div className="em-option-header">
                                <h5>{option.subtitle}</h5>
                                <div className="em-option-controls">
                                  <button 
                                    className="em-icon-button with-text"
                                    onClick={() => handleEditOption(option)}
                                  >
                                    <Edit size={16} />
                                    <span>Edit</span>
                                  </button>
                                  <button 
                                    className="em-icon-button with-text danger"
                                    onClick={() => handleDeleteOption(section.id, option)}
                                  >
                                    <Trash2 size={16} />
                                    <span>Delete</span>
                                  </button>
                                </div>
                              </div>
                              {option.file_url && (
                                <div className="em-file-preview">
                                  {option.file_type === 'image' ? (
                                    <div className="em-image-preview-container">
                                      <img src={option.file_url} alt={option.subtitle} className="em-preview-image" />
                                    </div>
                                  ) : (
                                    <div className="em-pdf-preview">
                                      {option.thumbnail_url ? (
                                        <div className="em-pdf-thumbnail-container">
                                          <img src={option.thumbnail_url} alt={`${option.subtitle} preview`} className="em-pdf-thumbnail" />
                                          <a href={option.file_url} target="_blank" rel="noopener noreferrer" className="em-view-pdf-link">
                                            <FileText size={16} />
                                            <span>View PDF</span>
                                          </a>
                                        </div>
                                      ) : (
                                        <div className="em-pdf-placeholder">
                                          <div className="em-pdf-icon">PDF</div>
                                          <a href={option.file_url} target="_blank" rel="noopener noreferrer" className="em-view-pdf-link">
                                            <FileText size={16} />
                                            <span>View PDF</span>
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="em-empty-options">
                        <p>No options added to this section yet</p>
						</div>
                    )}
                  </div>
                  
                  {/* Add new option form */}
                  <div className="em-add-option-form">
                    <h5>Add New Option</h5>
                    <div className="em-form-group">
                      <label className="em-label" htmlFor={`option-title-${section.id}`}>
                        Option Title <span className="em-required">*</span>
                      </label>
                      <input
                        id={`option-title-${section.id}`}
                        type="text"
                        placeholder="e.g., Silver Package, Premium Option"
                        value={newOption.sectionId === section.id ? newOption.subtitle : ''}
                        onChange={(e) => setNewOption({
                          sectionId: section.id,
                          subtitle: e.target.value,
                          file: newOption.file
                        })}
                        className="em-input"
                        disabled={uploadingOptionId === section.id}
                      />
                    </div>
                    
                    <div className="em-form-group">
                      <label className="em-label" htmlFor={`option-file-${section.id}`}>
                        Upload File (PDF or Image)
                        <InfoTooltip 
                          text="Upload a PDF menu or an image to showcase this option" 
                          variant="vendor"
                        />
                      </label>
                      <div className="em-file-input-container">
                        <input
                          id={`option-file-${section.id}`}
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={(e) => handleFileChange(e, 'option')}
                          className="em-file-input"
                          disabled={uploadingOptionId === section.id}
                        />
                        <div className="em-file-input-overlay">
                          <div className="em-file-input-button">
                            Choose File
                          </div>
                          <span className="em-file-name">
                            {newOption.sectionId === section.id && newOption.file 
                              ? newOption.file.name 
                              : 'No file chosen'}
                          </span>
                        </div>
                      </div>
                      <div className="em-file-size-info">
                        Maximum file size: 10MB. Supported formats: PDF, JPEG, PNG, GIF, WebP
                      </div>
                    </div>
                    
                    <button 
                      type="button"
                      onClick={() => handleAddOption(section.id)}
                      className="em-button primary"
                      disabled={uploadingOptionId === section.id}
                    >
                      {uploadingOptionId === section.id ? (
                        <>
                          <span className="em-spinner"></span>
                          <span>Adding...</span>
                        </>
                      ) : (
                        <>
                          <Plus size={16} />
                          <span>Add Option</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="em-live-events-view">
          {/* Live event creation/editing form */}
          <div className="em-card">
            <div className="em-card-header">
              <h2>{editingEvent ? 'Edit Event' : 'Add New Event'}</h2>
              {editingEvent && (
                <button 
                  type="button" 
                  onClick={() => {
                    setEditingEvent(null);
                    setNewEvent({
                      title: '',
                      description: '',
                      event_date: '',
                      event_time: '',
                      file: null
                    });
                  }} 
                  className="em-button-icon"
                  aria-label="Cancel editing"
                >
                  ×
                </button>
              )}
            </div>
            <div className="em-card-body">
              <form onSubmit={handleEventSubmit} className="em-form">
                <div className="em-form-group">
                  <label className="em-label" htmlFor="event-title">
                    Event Title <span className="em-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="event-title"
                    className="em-input"
                    placeholder="e.g., Live Jazz Night, Wine Tasting Event"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                    required
                  />
                </div>
                
                <div className="em-form-group">
                  <label className="em-label" htmlFor="event-description">
                    Description
                    <InfoTooltip 
                      text="Provide details about this event to inform your customers" 
                      variant="vendor"
                    />
                  </label>
                  <textarea
                    id="event-description"
                    className="em-input em-textarea"
                    placeholder="Enter additional details about this event (optional)"
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                    rows="3"
                  />
                </div>
                
                <div className="em-form-row">
                  <div className="em-form-group">
                    <label className="em-label" htmlFor="event-date">
                      Event Date <span className="em-required">*</span>
                    </label>
                    <input
                      type="date"
                      id="event-date"
                      className="em-input"
                      value={newEvent.event_date}
                      onChange={(e) => setNewEvent({...newEvent, event_date: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="em-form-group">
                    <label className="em-label" htmlFor="event-time">
                      Event Time <span className="em-required">*</span>
                    </label>
                    <input
                      type="time"
                      id="event-time"
                      className="em-input"
                      value={newEvent.event_time}
                      onChange={(e) => setNewEvent({...newEvent, event_time: e.target.value})}
                      required
                    />
                  </div>
                </div>
                
                <div className="em-form-group">
                  <label className="em-label" htmlFor="event-file">
                    Upload File (PDF or Image)
                    <InfoTooltip 
                      text="Upload a promotional image or PDF with event details" 
                      variant="vendor"
                    />
                  </label>
                  <div className="em-file-input-container">
                    <input
                      id="event-file"
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => handleFileChange(e, 'event')}
                      className="em-file-input"
                    />
                    <div className="em-file-input-overlay">
                      <div className="em-file-input-button">
                        Choose File
                      </div>
                      <span className="em-file-name">
                        {newEvent.file ? newEvent.file.name : 'No file chosen'}
                      </span>
                    </div>
                  </div>
                  <div className="em-file-size-info">
                    Maximum file size: 10MB. Supported formats: PDF, JPEG, PNG, GIF, WebP
                  </div>
                </div>
                
                <div className="em-form-actions">
                  {editingEvent && (
                    <button 
                      type="button" 
                      onClick={() => {
                        setEditingEvent(null);
                        setNewEvent({
                          title: '',
                          description: '',
                          event_date: '',
                          event_time: '',
                          file: null
                        });
                      }} 
                      className="em-button secondary"
                    >
                      Cancel
                    </button>
                  )}
                  <button 
                    type="submit" 
                    className="em-button primary"
                    disabled={isSavingEvent}
                  >
                    {isSavingEvent ? (
                      <>
                        <span className="em-spinner"></span> 
                        {editingEvent ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingEvent ? 'Update Event' : 'Create Event'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
          
          {/* Live events list */}
          <h3 className="em-section-title">Upcoming Events</h3>
          
          {liveEvents.length === 0 ? (
            <div className="em-empty-state">
              <div className="em-empty-icon">
                <Calendar size={40} />
              </div>
              <h3>No upcoming events scheduled</h3>
              <p>Create your first event to showcase special occasions at your business</p>
            </div>
          ) : (
            <div className="em-events-list">
              {liveEvents.map(event => (
                <div key={event.id} className="em-event-item">
                  <div className="em-event-header">
                    <h4>{event.title}</h4>
                    <div className="em-event-actions">
                      <button 
                        className="em-icon-button with-text"
                        onClick={() => handleEditEvent(event)}
                      >
                        <Edit size={16} />
                        <span>{editingEvent?.id === event.id ? 'Cancel Edit' : 'Edit'}</span>
                      </button>
                      <button 
                        className="em-icon-button with-text danger"
                        onClick={() => handleDeleteEvent(event)}
                      >
                        <Trash2 size={16} />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                  
                  {event.description && (
                    <p className="em-event-description">{event.description}</p>
                  )}
                  
                  <div className="em-event-datetime">
                    <Calendar size={16} />
                    <span>
                      {new Date(event.event_date).toLocaleDateString()} at {' '}
                      {new Date(event.event_date).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  
                  {event.file_url && (
                    <div className="em-file-preview">
                      {event.file_type === 'image' ? (
                        <div className="em-image-preview-container">
                          <img src={event.file_url} alt={event.title} className="em-preview-image" />
                        </div>
                      ) : (
                        <div className="em-pdf-preview">
                          {event.thumbnail_url ? (
                            <div className="em-pdf-thumbnail-container">
                              <img src={event.thumbnail_url} alt={`${event.title} preview`} className="em-pdf-thumbnail" />
                              <a href={event.file_url} target="_blank" rel="noopener noreferrer" className="em-view-pdf-link">
                                <FileText size={16} />
                                <span>View PDF</span>
                              </a>
                            </div>
                          ) : (
                            <div className="em-pdf-placeholder">
                              <div className="em-pdf-icon">PDF</div>
                              <a href={event.file_url} target="_blank" rel="noopener noreferrer" className="em-view-pdf-link">
                                <FileText size={16} />
                                <span>View PDF</span>
                              </a>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Confirmation Dialog */}
      {confirmDialog.show && (
        <div className="em-confirm-dialog-overlay">
          <div className="em-confirm-dialog">
            <h3>{confirmDialog.title}</h3>
            <p>{confirmDialog.message}</p>
            <div className="em-confirm-actions">
              <button 
                className="em-button secondary" 
                onClick={() => setConfirmDialog({ show: false })}
              >
                {confirmDialog.cancelText}
              </button>
              <button 
                className={`em-button ${confirmDialog.type}`} 
                onClick={confirmDialog.onConfirm}
              >
                {confirmDialog.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventsManagement;